import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {FormState} from '../../models/form-state.model';
import {PolicyService} from '../../services/policy.service';
import {Amendment} from '../../models/amendment.model';
import {DictionaryActions} from '../dictionary/dictionary.actions';
import {DictionaryCode} from '../../enums/dictionary-code.enum';
import {SalesCpiAsideAction} from './sales-cpi-aside.actions';
import {SalesCpiAction} from './sales-cpi.actions';
import {setFormValidationErrors} from '../../helpers/form.helper';
import {SalesCpiStateModel} from './sales-cpi.state';
import {throwError} from 'rxjs';
import {DictionaryItem} from '../../models/dictionary.model';


export interface SalesCpiAsideStateModel {
    form: FormState;
    step: number;
    amendment: Amendment;
    terminationReasons: DictionaryItem[];
}

@State<SalesCpiAsideStateModel>({
    name: SalesCpiAsideState.STATE_NAME,
    defaults: {
        form: new FormState(),
        step: null,
        amendment: null,
        terminationReasons: null,
    }
})
@Injectable()
export class SalesCpiAsideState {

    static readonly STATE_NAME = 'salesCpiAside';

    constructor(private policyService: PolicyService) {
    }

    @Selector()
    static step(state: SalesCpiAsideStateModel) {
        return state.step;
    }

    @Selector()
    static amendment(state: SalesCpiAsideStateModel) {
        return state.amendment;
    }

    @Selector()
    static formErrors(state: SalesCpiAsideStateModel) {
        return state.form.validationErrors;
    }

    @Selector()
    static terminationReasons(state: SalesCpiAsideStateModel) {
        return state.terminationReasons;
    }

    @Action(SalesCpiAsideAction.StartCancellationAmendment)
    startCancellationAmendment(ctx: StateContext<SalesCpiAsideStateModel>) {
        ctx.patchState({step: 1, amendment: null});
        ctx.dispatch(new SalesCpiAction.OpenCancellationAside());
    }

    @Action(SalesCpiAsideAction.Cancellation)
    cancellation(ctx: StateContext<SalesCpiAsideStateModel>, action: SalesCpiAsideAction.Cancellation) {
        return this.policyService.cancellation(action.policyId)
            .pipe(map(amendment => ctx.patchState({step: 2, amendment})),
                switchMap(() => ctx.dispatch(this.getPolicyActions(action.policyId))),
                catchError(error => {
                    ctx.dispatch(new SalesCpiAsideAction.FormFailure(error.error));
                    return throwError(error);
                }));
    }

    @Action(SalesCpiAsideAction.StartTerminationAmendment)
    startTerminationAmendment(ctx: StateContext<SalesCpiAsideStateModel>, {policyId}: SalesCpiAsideAction.StartTerminationAmendment) {
        return this.policyService.getTerminationReasons(policyId)
            .pipe(
                tap((terminationReasons) => {
                    ctx.patchState({step: 1, amendment: null, terminationReasons});
                    ctx.dispatch(new SalesCpiAction.OpenTerminationAside());
                }));
    }

    @Action(SalesCpiAsideAction.Termination)
    termination(ctx: StateContext<SalesCpiAsideStateModel>, action: SalesCpiAsideAction.Termination) {
        return this.policyService.termination(action.policyId, action.data)
            .pipe(map(amendment => ctx.patchState({step: 2, amendment})),
                switchMap(() => ctx.dispatch(this.getPolicyActions(action.policyId))),
                catchError(error => {
                    ctx.dispatch(new SalesCpiAsideAction.FormFailure(error.error));
                    return throwError(error);
                }));
    }

    @Action(SalesCpiAsideAction.StartBeneficiaryChangeAmendment)
    startBeneficiaryChangeAmendment(ctx: StateContext<SalesCpiAsideStateModel>) {
        ctx.patchState({step: 1, amendment: null});
        ctx.dispatch(new SalesCpiAction.OpenBeneficiaryChangeAside());
    }

    @Action(SalesCpiAsideAction.BeneficiaryChange)
    beneficiaryChange(ctx: StateContext<SalesCpiAsideStateModel>, action: SalesCpiAsideAction.BeneficiaryChange) {
        return this.policyService.beneficiaryChange(action.policyId, action.data)
            .pipe(map(amendment => ctx.patchState({step: 2, amendment})),
                switchMap(() => ctx.dispatch(this.getPolicyActions(action.policyId))),
                catchError(error => {
                    ctx.dispatch(new SalesCpiAsideAction.FormFailure(error.error));
                    return throwError(error);
                }));
    }

    @Action(SalesCpiAsideAction.StartPersonalDataChangeAmendment)
    startPersonalDataChangeAmendment(ctx: StateContext<SalesCpiAsideStateModel>) {
        const dictionaries = [
            DictionaryCode.GENDER,
            DictionaryCode.CITIZENSHIP,
            DictionaryCode.CLAUSES,
            DictionaryCode.DOCUMENT_TYPE,
        ];
        ctx.dispatch(new DictionaryActions.Download(dictionaries))
            .subscribe(() => {
                ctx.patchState({step: 1, amendment: null});
                ctx.dispatch(new SalesCpiAction.OpenPersonalDataChangeAside());
            });
    }

    @Action(SalesCpiAsideAction.PersonalDataChange)
    personalDataChange(ctx: StateContext<SalesCpiAsideStateModel>, action: SalesCpiAsideAction.PersonalDataChange) {
        return this.policyService.personalChange(action.policyId, action.data)
            .pipe(map(amendment => ctx.patchState({step: 2, amendment})),
                switchMap(() => ctx.dispatch(this.getPolicyActions(action.policyId))),
                catchError(error => {
                    ctx.dispatch(new SalesCpiAsideAction.FormFailure(error.error));
                    return throwError(error);
                }));
    }


    @Action(SalesCpiAsideAction.FormFailure)
    addUserFailure(ctx: StateContext<SalesCpiStateModel>, {validationErrors = []}: SalesCpiAction.FormFailure) {
        setFormValidationErrors(ctx, validationErrors, SalesCpiAsideState.STATE_NAME);
    }

    private getPolicyActions(id: number) {
        return [
            new SalesCpiAction.GetPolicy(id),
            new SalesCpiAction.GetAttachments(id),
        ];
    }
}
